import { useState } from "react";
import { ExternalLink, Lock, FileText, Landmark, Building2, CircleDashed } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const SOURCE_STYLE = {
  lumis_original: { cls: "bg-[#0A2A5E] text-white", Icon: FileText },
  government: { cls: "bg-[#DBEAFE] text-[#0A2A5E]", Icon: Landmark },
  organization: { cls: "bg-slate-200 text-slate-800", Icon: Building2 },
  catalog_only: { cls: "bg-slate-100 text-slate-600 border border-dashed border-slate-400", Icon: CircleDashed },
};
export const STATUS_STYLE = {
  existing: "bg-emerald-100 text-emerald-800",
  draft: "bg-[#FDECD0] text-[#7C3F00]",
  government: "bg-[#DBEAFE] text-[#0A2A5E]",
  organization: "bg-slate-200 text-slate-800",
  catalog_only: "bg-slate-100 text-slate-600",
  approved: "bg-emerald-600 text-white",
  retired: "bg-slate-300 text-slate-800",
  beta: "bg-[#0A2A5E] text-[#F5A623] border border-[#F5A623]",
};
export const BANNER_TEXT = { draft: "DRAFT — LUMIS HR REVIEW REQUIRED", beta: "BETA TEMPLATE — REVIEW BEFORE USE" };
export const BANNER_DETAIL = {
  draft: "— for preview and review only. Not approved, legally reviewed, compliant or ready for operational use.",
  beta: "— this is an original LUMIS HR template released in beta. Review and adapt it to your organization's requirements and circumstances before use.",
};
export const TEMPLATE_DISCLAIMER = "This LUMIS HR template provides general HR administrative support and is not legal advice. It does not replace or override your organization's approved forms, policies, procedures, collective bargaining agreements, plan documents or applicable government forms and requirements. Before use, the template must be reviewed and adapted using applicable federal, state and local requirements; organizational policies and procedures; collective bargaining agreements; plan documents; and individual circumstances. Use of this template does not guarantee legal compliance or a particular employment outcome.";

export function Pill({ children, className = "", testid }) {
  return <span data-testid={testid} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider ${className}`}>{children}</span>;
}

export function SourcePill({ source, label }) {
  const s = SOURCE_STYLE[source] || SOURCE_STYLE.catalog_only;
  return <Pill className={s.cls} testid={`source-${source}`}><s.Icon className="w-3 h-3" aria-hidden="true" />{label}</Pill>;
}

export function StatusPill({ status, label }) {
  return <Pill className={STATUS_STYLE[status] || STATUS_STYLE.catalog_only} testid={`status-${status}`}>{label}</Pill>;
}

export function AudiencePill({ audience, label }) {
  return <Pill className={audience === "manager" ? "bg-[#0A2A5E] text-white" : "bg-emerald-50 text-emerald-800 border border-emerald-200"} testid="audience-pill">{audience === "manager" && <Lock className="w-3 h-3" aria-hidden="true" />}{label}</Pill>;
}

export function DraftBanner({ text, compact, status = "draft" }) {
  const label = text || BANNER_TEXT[status];
  return (
    <div role="status" data-testid={status === "beta" ? "beta-banner" : "draft-banner"} className={`rounded-lg border border-[#F5A623] bg-[#FDECD0] text-[#7C3F00] font-bold ${compact ? "text-[11px] px-2 py-1" : "text-sm px-4 py-2.5"}`}>
      {label} {!compact && <span className="font-normal">{BANNER_DETAIL[status]}</span>}
    </div>
  );
}

// Government form: opens the official agency page only after an explicit "leaving LUMIS HR" confirmation
export function GovLink({ entry, leavingText, children, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" data-testid={`gov-open-${entry.id}`} onClick={() => setOpen(true)} className={className}>{children}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="gov-leaving-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-[#0A2A5E]">{entry.form_number} — {entry.title.split(" — ")[1] || entry.title}</DialogTitle>
            <DialogDescription>{leavingText}</DialogDescription>
          </DialogHeader>
          <dl className="text-sm text-slate-700 space-y-1">
            <div><dt className="inline font-semibold">Issuing agency: </dt><dd className="inline">{entry.agency}</dd></div>
            <div><dt className="inline font-semibold">Version: </dt><dd className="inline">{entry.version}</dd></div>
            <div><dt className="inline font-semibold">Link last verified: </dt><dd className="inline">{entry.link_verified}</dd></div>
            {entry.note && <p className="text-xs text-slate-500 pt-1">{entry.note}</p>}
            <p className="text-xs text-slate-500">LUMIS HR does not collect or store information entered into the external government form.</p>
          </dl>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="gov-cancel-btn">Cancel</Button>
            <a href={entry.url} target="_blank" rel="noopener noreferrer" data-testid="gov-continue-link" onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center h-10 px-4 rounded-md bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white text-sm font-medium">Continue to {entry.agency.split(",")[0]} <ExternalLink className="w-4 h-4 ml-2" /></a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
